import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, ChevronDown, CircleDollarSign, Clock3, Users } from "lucide-react";
import axios from "axios";
import Footer from "./Footer";
import Header from "./header";
import { CATEGORY_LABELS, QUEST_LISTING_PATH, formatAccessList, formatEntryFee, formatQuestDateRange, getQuestBrief, getQuestImages, getQuestStatus, parseQuestEventFlow, parseQuestSectionContent } from "../utils/questDisplay";

const SECTION_DEFINITIONS = [
  { key: "welcomeMessage", slug: "welcome", label: "Welcome" },
  { key: "challengeBrief", slug: "brief", label: "The brief" },
  { key: "challengePrompt", slug: "prompt", label: "Your prompt" },
  { key: "eligibility", slug: "eligibility", label: "Eligibility" },
  { key: "exampleSubjects", slug: "examples", label: "Example subjects" },
  { key: "storytellingGuidance", slug: "story", label: "Storytelling guidance" },
  { key: "technicalTips", slug: "technical", label: "Technical tips" },
  { key: "judgingCriteria", slug: "judging", label: "How entries are judged" },
  { key: "usefulInformation", slug: "information", label: "Useful information" },
  { key: "rules", slug: "rules", label: "Rules", disclosure: true },
  { key: "rightsLicense", slug: "rights", label: "Rights & license", disclosure: true },
];

const normalizeSection = (section, index) => ({
  ...section,
  slug: section.slug || section.key || `section-${index + 1}`,
  label: section.label || `Section ${index + 1}`,
  paragraphs: Array.isArray(section.paragraphs) ? section.paragraphs.filter(Boolean) : [],
  items: Array.isArray(section.items) ? section.items.filter((item) => item?.title || item?.body) : [],
});

const buildSections = (quest) => {
  const structured = quest?.questSections;
  const hasStructuredContent = structured && SECTION_DEFINITIONS.some(({ key }) => String(structured[key] || "").trim());
  if (hasStructuredContent) {
    return SECTION_DEFINITIONS.flatMap((definition) => {
      const value = String(structured[definition.key] || "").trim();
      if (!value) return [];
      const parsed = parseQuestSectionContent(value);
      return [normalizeSection({ ...definition, paragraphs: parsed.paragraphs, items: parsed.items }, 0)];
    });
  }
  return parseQuestEventFlow(quest?.description).map((section, index) => normalizeSection({ ...section, slug: section.key }, index));
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3 border-b border-[#DFE6E4] py-4 last:border-b-0">
    <Icon aria-hidden="true" className="mt-0.5 shrink-0 text-[#007C86]" size={19} strokeWidth={1.8} />
    <div className="min-w-0">
      <dt className="font-outfit text-xs font-bold uppercase tracking-[0.12em] text-[#667476]">{label}</dt>
      <dd className="mt-1 font-outfit text-[15px] font-semibold leading-6 text-[#182124]">{value}</dd>
    </div>
  </div>
);

const SubmitButton = ({ questId, onNavigate, inverse = false }) => (
  <button type="button" onClick={() => onNavigate(`/submissions?challenge=${questId}`)} className={`group flex w-full items-center justify-center gap-2 rounded-md px-5 py-3.5 font-outfit text-base font-bold transition duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#00D7E5]/40 ${inverse ? "bg-white text-[#075A62] hover:bg-[#EAFBFA]" : "bg-[#006D75] text-white shadow-[0_10px_24px_rgba(0,109,117,0.18)] hover:bg-[#005B62]"}`}>
    Submit entry <ArrowRight aria-hidden="true" className="transition-transform group-hover:translate-x-1" size={18} />
  </button>
);

const SectionContent = ({ section }) => (
  <div className="mt-5 space-y-5">
    {section.paragraphs.map((paragraph, index) => <p key={`${section.slug}-paragraph-${index}`} className="whitespace-pre-line font-outfit text-base leading-[1.78] text-[#303B3E] md:text-[17px]">{paragraph}</p>)}
    {section.items.length > 0 && (
      <ul className="space-y-4">
        {section.items.map((item, index) => (
          <li key={`${section.slug}-item-${index}`} className="flex gap-3.5 font-outfit text-base leading-7 text-[#303B3E] md:text-[17px]">
            <CheckCircle2 aria-hidden="true" className="mt-1 shrink-0 text-[#007C86]" size={19} strokeWidth={2} />
            <span>{item.title && <strong className="font-semibold text-[#182124]">{item.title}</strong>}{item.title && item.body && <span aria-hidden="true">{" \u2014 "}</span>}{item.body}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

function Challengedetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchQuestById = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`/api/challenges/${id}`);
        if (!active) return;
        if (res.data.success) setQuest(res.data.quest);
        else setError(res.data.message || "Quest not found");
      } catch (err) {
        console.error("Error fetching quest:", err);
        if (active) setError(err?.response?.data?.message || "Quest not found");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchQuestById();
    return () => { active = false; };
  }, [id]);

  const sections = useMemo(() => buildSections(quest), [quest]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8EEDC] px-5 pt-32" role="status" aria-live="polite">
      <span className="sr-only">Loading quest details</span>
      <div className="mx-auto max-w-6xl animate-pulse motion-reduce:animate-none"><div className="h-72 rounded-sm bg-[#E7DDCB]" /><div className="mx-auto mt-10 h-7 w-48 bg-[#E7DDCB]" /><div className="mx-auto mt-5 h-4 max-w-2xl bg-[#E7DDCB]" /><div className="mx-auto mt-3 h-4 max-w-xl bg-[#E7DDCB]" /></div>
    </div>
  );

  if (error && !quest) return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8EEDC] px-5"><div className="max-w-md text-center">
      <p className="font-outfit text-sm font-bold uppercase tracking-[0.14em] text-[#007C86]">Quest unavailable</p>
      <h1 className="mt-3 font-cinzel text-3xl text-[#182124]">Content not found</h1>
      <p className="mt-4 font-outfit text-base leading-7 text-[#526164]">{error}</p>
      <button type="button" onClick={() => navigate(QUEST_LISTING_PATH)} className="mt-7 inline-flex items-center gap-2 rounded-md bg-[#006D75] px-5 py-3 font-outfit font-bold text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#00D7E5]/40"><ArrowLeft aria-hidden="true" size={18} /> Back to quests</button>
    </div></div>
  );

  const status = getQuestStatus(quest || {});
  const brief = getQuestBrief(quest?.description);
  const questImages = getQuestImages(quest);
  const heroImage = questImages[0];
  const supportingImages = questImages.slice(1);
  const access = formatAccessList(quest?.accessibility).join(", ");

  return (
    <><Header />
      <main className="min-h-screen bg-[#F8EEDC] text-[#182124]">
        <motion.header initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="relative flex min-h-[310px] items-end overflow-hidden md:min-h-[380px]">
          {heroImage ? <img src={heroImage} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-[#17383B]" aria-hidden="true" />}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,22,24,0.92)_0%,rgba(7,22,24,0.67)_55%,rgba(7,22,24,0.36)_100%)]" aria-hidden="true" />
          <div className="relative mx-auto w-full max-w-7xl px-5 pb-9 pt-24 sm:px-8 md:pb-12 lg:px-10">
            <button type="button" onClick={() => navigate(QUEST_LISTING_PATH)} className="mb-8 inline-flex items-center gap-2 font-outfit text-sm font-semibold text-white/90 underline-offset-4 transition hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"><ArrowLeft aria-hidden="true" size={18} /> Back to all quests</button>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="rounded-sm border border-white/30 bg-black/25 px-3 py-1.5 font-outfit text-xs font-bold uppercase tracking-[0.12em] text-white">{CATEGORY_LABELS[quest?.category] || "Quest"}</span>
              <span className={`rounded-sm border px-3 py-1.5 font-outfit text-xs font-bold uppercase tracking-[0.1em] ${status.className}`}>{status.label}</span>
            </div>
            <h1 className="mt-4 max-w-4xl text-balance font-cinzel text-3xl leading-[1.16] text-white sm:text-4xl md:text-5xl">{quest?.title}</h1>
          </div>
        </motion.header>

        <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 md:py-12 lg:px-10">
          {sections.length > 0 && (
            <nav aria-label="On this page" className="mb-8 border-y border-[#C9D4D1] py-4 md:mb-12">
              <div className="mb-3 flex items-center justify-between gap-4"><p className="font-outfit text-xs font-bold uppercase tracking-[0.14em] text-[#526164]">On this page</p><p className="font-outfit text-xs text-[#667476]">{sections.length} sections</p></div>
              <ol className="flex snap-x gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
                {sections.map((section, index) => <li key={`nav-${section.slug}`} className="shrink-0 snap-start"><a href={`#${section.slug}`} className="inline-flex items-center gap-2 border-l-2 border-[#8FC6C6] bg-white/55 px-3 py-2 font-outfit text-sm font-semibold text-[#28474A] transition hover:border-[#007C86] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007C86]"><span className="text-xs text-[#007C86]">{String(index + 1).padStart(2, "0")}</span>{section.label}</a></li>)}
              </ol>
            </nav>
          )}

          <div className="grid items-start gap-9 lg:grid-cols-[minmax(0,760px)_minmax(280px,340px)] lg:justify-between lg:gap-14">
            <article className="order-2 min-w-0 lg:order-1">
              <div className="mb-10 border-b border-[#C9D4D1] pb-8 md:mb-14">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.16em] text-[#007C86]">Quest field guide</p>
                <h2 className="mt-3 font-cinzel text-2xl leading-tight text-[#182124] sm:text-3xl">Everything you need to take part</h2>
                <p className="mt-4 max-w-2xl font-outfit text-base leading-7 text-[#526164] md:text-[17px]">Read the creative brief from start to finish, or use the section guide above to jump to what you need.</p>
              </div>

              {sections.length > 0 ? <div>{sections.map((section, index) => {
                const heading = <div className="flex items-start gap-4"><span className="mt-1 font-outfit text-xs font-bold tracking-[0.12em] text-[#007C86]">{String(index + 1).padStart(2, "0")}</span><h2 className="font-cinzel text-2xl leading-tight text-[#182124] md:text-[28px]">{section.label}</h2></div>;
                if (section.disclosure) return <details key={section.slug} id={section.slug} className="group scroll-mt-28 border-t border-[#C9D4D1] py-6 first:border-t-0 md:py-8"><summary className="flex cursor-pointer list-none items-start justify-between gap-4 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007C86] [&::-webkit-details-marker]:hidden">{heading}<ChevronDown aria-hidden="true" className="mt-1 shrink-0 text-[#007C86] transition-transform group-open:rotate-180" size={22} /></summary><div className="md:pl-10"><SectionContent section={section} /></div></details>;
                return <section key={section.slug} id={section.slug} className="scroll-mt-28 border-b border-[#D5DDDB] pb-10 pt-1 first:pt-0 last:border-b-0 md:pb-14 md:pt-3">{heading}<div className="md:pl-10"><SectionContent section={section} /></div></section>;
              })}</div> : <section><h2 className="font-cinzel text-2xl text-[#182124]">Quest brief</h2><div className="mt-5 space-y-5">{(brief.paragraphs.length ? brief.paragraphs : ["Full details will be published soon."]).map((paragraph, index) => <p key={`brief-${index}`} className="whitespace-pre-line font-outfit text-base leading-[1.78] text-[#303B3E] md:text-[17px]">{paragraph}</p>)}</div></section>}

              {supportingImages.length > 0 && <section aria-labelledby="gallery-title" className="mt-12 border-t border-[#C9D4D1] pt-10 md:mt-16 md:pt-12">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.16em] text-[#007C86]">Visual references</p><h2 id="gallery-title" className="mt-2 font-cinzel text-2xl text-[#182124]">Quest gallery</h2>
                <div className={`mt-6 grid gap-5 ${supportingImages.length > 1 ? "sm:grid-cols-2" : ""}`}>{supportingImages.map((image, index) => <img key={`${image}-${index}`} src={image} alt={`${quest?.title || "Quest"} visual reference ${index + 1}`} className="aspect-[4/3] w-full rounded-sm object-cover shadow-[0_10px_28px_rgba(24,33,36,0.12)]" />)}</div>
              </section>}
            </article>

            <aside aria-labelledby="at-a-glance" className="order-1 lg:order-2 lg:sticky lg:top-28"><div className="border-t-4 border-[#007C86] bg-[#FFFDF8] p-5 shadow-[0_12px_35px_rgba(53,65,65,0.09)] sm:p-6">
              <div className="flex items-center justify-between gap-3"><h2 id="at-a-glance" className="font-cinzel text-xl text-[#182124]">At a glance</h2><span className="font-outfit text-xs font-bold uppercase tracking-[0.1em] text-[#007C86]">{status.label}</span></div>
              <dl className="mt-3"><DetailRow icon={CalendarDays} label="Schedule" value={formatQuestDateRange(quest?.startDate, quest?.endDate)} /><DetailRow icon={CircleDollarSign} label="Entry fee" value={formatEntryFee(quest?.entryFeeCents, quest?.currency)} /><DetailRow icon={Users} label="Access & eligibility" value={access} /><DetailRow icon={Clock3} label="Current status" value={status.label} /></dl>
              <div className="mt-5"><SubmitButton questId={quest?._id} onNavigate={navigate} /></div><p className="mt-3 text-center font-outfit text-xs leading-5 text-[#667476]">Review the requirements before sending your work.</p>
            </div></aside>
          </div>

          <section className="mt-14 overflow-hidden bg-[#075A62] px-6 py-9 text-white sm:px-10 md:mt-20 md:flex md:items-center md:justify-between md:gap-10 md:py-11" aria-labelledby="final-cta-title">
            <div className="max-w-2xl"><p className="font-outfit text-xs font-bold uppercase tracking-[0.16em] text-[#8FF2F3]">Ready when you are</p><h2 id="final-cta-title" className="mt-2 font-cinzel text-2xl leading-tight sm:text-3xl">Turn your idea into an entry.</h2><p className="mt-3 font-outfit text-base leading-7 text-white/85">You have the brief. Now show us where your creativity takes it.</p></div>
            <div className="mt-6 w-full shrink-0 md:mt-0 md:w-52"><SubmitButton questId={quest?._id} onNavigate={navigate} inverse /></div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
export default Challengedetails;
